import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { Paragraph, Anchor, List, DataSelect, Table, Pagination } from "@zillow/constellation-10";
import { dollar } from "../../node_modules/@zillow/formatters/dist/formatters.esm.esm.js";
import { last } from "lodash";
import { useState, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { handleFieldClickEvent, handleFieldChangeEvent } from "./AmortizationCalculator.Clickstream.esm.js";
import { useLastValidAmortizationValues, getZillowURL } from "./AmortizationCalculator.utils.esm.js";
import { FORM_FIELD } from "./constants.esm.js";
const AMORTIZATION_BREAKDOWN_ID = "amortization-breakdown";
const TABLE_DISPLAY_MODE = {
  MONTH: "month",
  YEAR: "year"
};
const OPTIONS = {
  "Show by month": TABLE_DISPLAY_MODE.MONTH,
  "Show by year": TABLE_DISPLAY_MODE.YEAR
};
const MODES = {
  DATE_BASED: "date"
};
const getPageSizeForShowByValue = (showBy) => showBy === TABLE_DISPLAY_MODE.YEAR ? 10 : 12;
const AmortizationScheduleTable = ({
  mode = MODES.DATE_BASED
}) => {
  const form = useFormContext();
  const amortization = useLastValidAmortizationValues(form).calculations.amortization;
  const [showBy, setShowBy] = useState(TABLE_DISPLAY_MODE.MONTH);
  const [pageSize, setPageSize] = useState(getPageSizeForShowByValue(showBy));
  const [currentPage, setCurrentPage] = useState(0);
  useEffect(() => {
    setCurrentPage(0);
    setPageSize(getPageSizeForShowByValue(showBy));
  }, [showBy]);
  const filteredAmortization = useMemo(() => showBy === TABLE_DISPLAY_MODE.YEAR ? amortization.filter((x) => x.month % 12 - 1 === 0) : amortization, [showBy, amortization]);
  const handleDataSelectChange = (e) => {
    const value = e.target.value;
    if (value === "year") {
      handleFieldChangeEvent("Show by year", FORM_FIELD);
    } else if (value === "month") {
      handleFieldChangeEvent("Show by month", FORM_FIELD);
    }
    setShowBy(value);
  };
  const amortizationPage = useMemo(() => filteredAmortization.slice(currentPage * pageSize, (currentPage + 1) * pageSize), [filteredAmortization, pageSize, currentPage]);
  const lastAmortization = last(amortization);
  const totalPrincipal = (lastAmortization == null ? void 0 : lastAmortization.totalPrincipal) ?? 0;
  const totalInterest = (lastAmortization == null ? void 0 : lastAmortization.totalInterest) ?? 0;
  const breakdownContent = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(Paragraph, { css: {
      marginTop: "tighter",
      marginBottom: "tighter"
    }, children: [
      "Our mortgage amortization schedule makes it easy to see how much of your mortgage payment will go toward paying interest and principal over your loan term. You can view amortization by month or year. Keep in mind, your monthly mortgage payment may also include",
      " ",
      /* @__PURE__ */ jsxs(Anchor, { href: getZillowURL("mortgage-learning/what-is-piti/"), children: [
        "property taxes and home insurance",
        " "
      ] }),
      " ",
      "- which aren't included in this amortization schedule, since the payments may fluctuate throughout your loan term."
    ] }),
    /* @__PURE__ */ jsxs(List.Root, { "aria-live": "polite", "aria-atomic": "true", css: {
      marginTop: "tighter",
      marginBottom: "tighter"
    }, children: [
      /* @__PURE__ */ jsxs(List.Item, { children: [
        "Total principal payments: ",
        dollar(totalPrincipal)
      ] }),
      /* @__PURE__ */ jsxs(List.Item, { children: [
        "Total interest payments: ",
        dollar(totalInterest)
      ] })
    ] }),
    /* @__PURE__ */ jsx(DataSelect, { css: {
      marginBottom: "default"
    }, "aria-label": "Amortization schedule breakdown show by time length dropdown", defaultValue: "month", data: OPTIONS, onChange: handleDataSelectChange }),
    /* @__PURE__ */ jsxs(Table.Root, { appearance: "zebra", "aria-label": "Amortization schedule breakdown table", "aria-live": "polite", "aria-atomic": "true", children: [
      /* @__PURE__ */ jsx(Table.Header, { children: /* @__PURE__ */ jsx(Table.Row, { children: mode === MODES.DATE_BASED ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Table.HeaderCell, { children: "Date" }),
        /* @__PURE__ */ jsx(Table.HeaderCell, { children: "Interest" }),
        /* @__PURE__ */ jsx(Table.HeaderCell, { children: "Principal" }),
        /* @__PURE__ */ jsx(Table.HeaderCell, { children: "Principal remaining" })
      ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Table.HeaderCell, { children: "Month" }),
        /* @__PURE__ */ jsx(Table.HeaderCell, { children: "Interest" }),
        /* @__PURE__ */ jsx(Table.HeaderCell, { children: "Principal" }),
        /* @__PURE__ */ jsx(Table.HeaderCell, { children: "Principal remaining" })
      ] }) }) }),
      /* @__PURE__ */ jsx(Table.Body, { children: amortizationPage.map((item, index) => /* @__PURE__ */ jsxs(Table.Row, { children: [
        /* @__PURE__ */ jsx(Table.Cell, { children: mode === MODES.DATE_BASED ? item.formattedDate : item.month }),
        /* @__PURE__ */ jsx(Table.Cell, { children: dollar(item.monthlyInterest) }),
        /* @__PURE__ */ jsx(Table.Cell, { children: dollar(item.monthlyPrincipal) }),
        /* @__PURE__ */ jsx(Table.Cell, { children: dollar(item.remainingBalance) })
      ] }, `${item.remainingBalance}-${index}`)) })
    ] }),
    /* @__PURE__ */ jsx(Pagination, { css: {
      marginTop: "default"
    }, "aria-label": `Amortization breakdown pagination navigation by ${showBy}`, currentPageIndex: currentPage, onPageSelected: setCurrentPage, totalPages: Math.ceil(filteredAmortization.length / pageSize), onClick: () => handleFieldClickEvent("Prev/Next", "button") })
  ] });
  return /* @__PURE__ */ jsx("div", { id: AMORTIZATION_BREAKDOWN_ID, children: breakdownContent });
};
export {
  AMORTIZATION_BREAKDOWN_ID,
  MODES,
  AmortizationScheduleTable as default
};
